import React, { FC, useEffect, useState } from 'react'
import _ from 'lodash'
// lib
import { isValidDate, parseDate } from 'data/lib/dates.ts'
// data
import api_match from "data/interface/api"
import attributeService from "data/attributes"
import { local } from 'data/lib/local-json'
import folderService from 'data/folder'
// styles
import { InputText_s, ListboxSection_s, ListboxHeaderSort_s } from 'styles/input-styles'
import { Button_s, Label_s } from 'styles/app-styles'
import { DateText_s } from 'styles/report-styles'
import { PropertyMetrics_s } from './report-styles'
import ListboxInput from "components/inputs/listbox"
// types
import { FolderReportProps, ReportFormParameters } from './reports'

const displayName = 'Property Metrics'

type PropertyMetricsParameters = ReportFormParameters & {
  begindate: string // MM/DD/YYYY
  report: string
  term: number
  prop_info: string[]
  valuation_metrics: string[]
  cf_metrics: string[]
  attribute_info?: string[]
}

export const propertyFilter = (property) => property.type === 'Commercial'

export const PropertyMetrics: FC<FolderReportProps> = (props) => {
  const currentFolder = _.last(local('folderPath'))
  const [isAMSubfoler, setIsAMSubfolder] = useState<boolean>(false)
  const [startdate, setStartDate] = useState<string>(props.reportbegindate)
  const [term, setTerm] = useState<number>(props.term)
  const [termOnce, setTermOnce] = useState<boolean>(false)
  const [attributes, setAttributes] = useState<object[]>([])
  const [attribute_info, setAttributeInfo] = useState<string[]>([])
  const [prop_info, setPropInfo] = useState<string[]>(['address', 'city', 'state', 'postalcode', 'area', 'units_rooms'])
  const [valuation_metrics, setValuationMetrics] = useState<string[]>(['prop_value', 'term', 'discount_rate', 'cap_rate', 'cost_of_sales', 'valuation_type'])
  const [cf_metrics, setCashFlowMetrics] = useState<string[]>(['egr', 'noi', 'ncf', 'occ_percent', 'exp_percent'])

  if (!term && props.term && !termOnce) {
    setTerm(props.term)
    setTermOnce(true)
  }

  useEffect(() => {
    currentFolder && folderService.isAssetManagementSubfolder(currentFolder).then((res) => {
      setIsAMSubfolder(res)
      attributeService.fetchAttributes()
    })

    const attributeSubscription = attributeService.stream().subscribe(data => {
      api_match(
        data,
        init  => {},
        ok    => {
          setAttributes(data.value)
          setAttributeInfo(data.value.map(each => each['code']))
        },
        err   => {},
        fetch => {}
      )
    })

    return function cleanup() {
      attributeSubscription.unsubscribe()
    }
  }, [currentFolder])

  useEffect(() => {
    const valid = isValidDate(startdate) && !_.isNil(term) && term >= 3

    props.setParameters(getParameters())
    props.setValid(valid)
  }, [startdate, term, prop_info, valuation_metrics, cf_metrics, attribute_info])

  function getParameters(): PropertyMetricsParameters {
    let params = {
      begindate: startdate,
      report: 'folder_property_metrics',
      term: +term,
      prop_info,
      valuation_metrics,
      cf_metrics
    }
    if (isAMSubfoler){ params['attribute_info'] = attribute_info } 
    else { params['attribute_info'] = [] }
    return params
  }

  function selectAll(selected, options, updateFn):void {
    if(selected?.length > 0 && selected?.length <= options.length) updateFn([])
    else updateFn(_.map(options, (group) => group.value))
  }

  const propInfoOptions = [
    { label: 'Address', value: 'address' },
    { label: 'City', value: 'city' },
    { label: 'State', value: 'state' },
    { label: 'Zip', value: 'postalcode' },
    { label: 'Property Area', value: 'area' },
    { label: 'Units/Rooms', value: 'units_rooms' }
  ]
  const valuationMetricOptions = [
    { label: 'Property Value', value: 'prop_value' },
    { label: 'Term', value: 'term' },
    { label: 'Discount Rate', value: 'discount_rate' },
    { label: 'Cap Rate', value: 'cap_rate' },
    { label: 'Cost of Sales', value: 'cost_of_sales' },
    { label: 'Valuation Type', value: 'valuation_type' }
  ]
  const cashFlowMetricOptions = [
    { label: 'EGR', value: 'egr' },
    { label: 'NOI', value: 'noi' },
    { label: 'NCF', value: 'ncf' },
    { label: 'Occupancy %', value: 'occ_percent' },
    { label: 'Expiration %', value: 'exp_percent' }
  ]
  const attributeOptions = attributes.map(attribute => { return { label: attribute['code'], value: attribute['code'] }})

  return (
    <PropertyMetrics_s>
      <div className="filled-form-row">
        <div>
            <Label_s>Start Date</Label_s>
            <DateText_s
              value={parseDate(startdate).format('MM/DD/YYYY')}
              onChange={setStartDate}
              onBlur={setStartDate}
              persist={setStartDate}
              withoutDays
              disabled={cf_metrics?.length === 0}
            />
        </div>
        <div>
            <Label_s>Term</Label_s>
            <InputText_s
              keyfilter="pnum"
              onChange={(e) => setTerm(e.target.value)}
              value={term}
              disabled={cf_metrics?.length === 0}
            />
        </div>
      </div>
      <div className="select-lists">
        <ListboxSection_s>
          <ListboxHeaderSort_s>
            <label>Property Info</label>
            <Button_s onClick={()=>selectAll(prop_info, propInfoOptions, setPropInfo)} style={{marginBottom: 10}}>{prop_info.length > 0 ? 'Deselect All' : 'Select All'}</Button_s>
          </ListboxHeaderSort_s>
          <ListboxInput
            field="prop_info"
            value={prop_info}
            multiple={true}
            persist={(field:string, val:string[]) => setPropInfo(val)} 
            options={propInfoOptions}
          />
        </ListboxSection_s>
        <ListboxSection_s>
          <ListboxHeaderSort_s>
            <label>Valuation Metrics</label>
            <Button_s onClick={()=>selectAll(valuation_metrics, valuationMetricOptions, setValuationMetrics)} style={{marginBottom: 10}}>{valuation_metrics.length > 0 ? 'Deselect All' : 'Select All'}</Button_s>
          </ListboxHeaderSort_s>
          <ListboxInput
            field="valuation_metrics"
            value={valuation_metrics}
            multiple={true}
            persist={(field:string, val:string[]) => setValuationMetrics(val)} 
            options={valuationMetricOptions}
          />
        </ListboxSection_s>
        { attributes && attributes.length > 0 && isAMSubfoler && <ListboxSection_s>
          <ListboxHeaderSort_s>
            <label>Attribute Info</label>
            <Button_s onClick={()=>selectAll(attribute_info, attributeOptions, setAttributeInfo)} style={{marginBottom: 10}}>{attribute_info.length > 0 ? 'Deselect All' : 'Select All'}</Button_s>
          </ListboxHeaderSort_s>
          <ListboxInput
            field="attribute_info"
            value={attribute_info}
            multiple={true}
            persist={(field:string, val:string[]) => setAttributeInfo(val)} 
            options={attributeOptions || null}
          />
        </ListboxSection_s> }
        <ListboxSection_s>
          <ListboxHeaderSort_s>
            <label>Cash Flow Metrics</label>
            <Button_s onClick={()=>selectAll(cf_metrics, cashFlowMetricOptions, setCashFlowMetrics)} style={{marginBottom: 10}}>{cf_metrics.length > 0 ? 'Deselect All' : 'Select All'}</Button_s>
          </ListboxHeaderSort_s>
          <ListboxInput
            field="cf_metrics"
            value={cf_metrics}
            multiple={true}
            persist={(field:string, val:string[]) => setCashFlowMetrics(val)} 
            options={cashFlowMetricOptions}
          />
        </ListboxSection_s>
      </div>
    </PropertyMetrics_s>
  )
}

PropertyMetrics.displayName = displayName
export default PropertyMetrics